// frontend/src/Contactos.js
// ============================================
// PAGINA DE CONTACTO - UrbanQ
// Estilo moderno con tema oscuro cyan/purple
// Integracion con backend para envio de emails
// ============================================

import React, { useState, useEffect } from 'react';
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaClock,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationCircle,
  FaBuilding
} from 'react-icons/fa';
import { API_ENDPOINTS } from './config/api';
import './Contactos.css';

function Contactos() {
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    asunto: '',
    mensaje: ''
  });

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Limpiar mensajes despues de un tiempo
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 6000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Manejar cambios en inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones basicas
    if (!formData.nombre.trim() || !formData.email.trim() ||
        !formData.asunto.trim() || !formData.mensaje.trim()) {
      setError('Por favor completa todos los campos');
      return;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Por favor ingresa un email valido');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.contactSend, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        setFormData({
          nombre: '',
          email: '',
          asunto: '',
          mensaje: ''
        });
      } else {
        setError(data.message || 'Error al enviar el mensaje');
      }
    } catch (err) {
      console.error('Error enviando mensaje:', err);
      setError('Error de conexion. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contactos-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <h1>Contactanos</h1>
        <p>
          Estamos aqui para ayudarte. Envianos tu mensaje y te responderemos
          a la brevedad posible.
        </p>
      </section>

      {/* Contenido Principal */}
      <div className="contact-container">
        <div className="contact-grid">

          {/* Card de Informacion */}
          <div className="contact-info-card">
            <h2 className="contact-info-title">Informacion de Contacto</h2>

            <div className="contact-info-item">
              <div className="contact-info-icon">
                <FaPhone />
              </div>
              <div className="contact-info-content">
                <h4>Telefono</h4>
                <p>
                  <a href="tel:+593995346662">+593 99 534 6662</a>
                </p>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon">
                <FaEnvelope />
              </div>
              <div className="contact-info-content">
                <h4>Email</h4>
                <p>
                  <a href="mailto:admin@nextunicodecorp.com">admin@nextunicodecorp.com</a>
                </p>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon">
                <FaMapMarkerAlt />
              </div>
              <div className="contact-info-content">
                <h4>Ubicacion</h4>
                <p>Quito, Ecuador</p>
              </div>
            </div>

            {/* Horario */}
            <div className="contact-schedule">
              <h4>
                <FaClock /> Horario de Atencion
              </h4>
              <p>Lunes a Viernes: 9:00 AM - 6:00 PM</p>
              <p>Sabados: 9:00 AM - 1:00 PM</p>
            </div>
          </div>

          {/* Card del Formulario */}
          <div className="contact-form-card">
            <h2 className="contact-form-title">Envianos un Mensaje</h2>

            {/* Alertas */}
            {success && (
              <div className="contact-alert contact-alert-success">
                <FaCheckCircle />
                <p>Mensaje enviado correctamente. Te contactaremos pronto.</p>
              </div>
            )}

            {error && (
              <div className="contact-alert contact-alert-error">
                <FaExclamationCircle />
                <p>{error}</p>
              </div>
            )}

            {/* Formulario */}
            <form onSubmit={handleSubmit}>
              <div className="contact-form-row">
                <div className="contact-form-group">
                  <label className="contact-form-label">Nombre completo</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    className="contact-form-input"
                    placeholder="Tu nombre"
                    disabled={loading}
                  />
                </div>

                <div className="contact-form-group">
                  <label className="contact-form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="contact-form-input"
                    placeholder="tu@email.com"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="contact-form-group">
                <label className="contact-form-label">Asunto</label>
                <input
                  type="text"
                  name="asunto"
                  value={formData.asunto}
                  onChange={handleChange}
                  className="contact-form-input"
                  placeholder="Motivo de tu mensaje"
                  disabled={loading}
                />
              </div>

              <div className="contact-form-group">
                <label className="contact-form-label">Mensaje</label>
                <textarea
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  className="contact-form-textarea"
                  placeholder="Escribe tu mensaje aqui..."
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="contact-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="contact-spinner"></span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <FaPaperPlane />
                    Enviar Mensaje
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Seccion del Mapa (placeholder) */}
        <section className="contact-map-section">
          <div className="contact-map-card">
            <h3>Nuestra Ubicacion</h3>
            <div className="contact-map-placeholder">
              <FaBuilding />
              <p>Quito, Ecuador</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Contactos;
